/**
 * Secure Deal Box - Constants & Translations
 */

import type { DocumentCategory, DocumentType, PermissionRule, VisibilityRole } from '@/types/deal-box.types';

// Document type labels
export const DOCUMENT_TYPE_LABELS: Record<string, Record<DocumentType, string>> = {
  en: {
    nda_signed: 'Signed NDA',
    loi: 'Letter of Intent',
    ioi: 'Indication of Interest',
    pnl_annual: 'Annual P&L',
    balance_sheet: 'Balance Sheet',
    tax_report: 'Tax Report',
    cim: 'CIM',
    evaluation: 'Valuation',
    contract: 'Contract',
    license: 'License',
    other: 'Other',
  },
  fr: {
    nda_signed: 'NDA signé',
    loi: 'Lettre d\'intention',
    ioi: 'Indication d\'intérêt',
    pnl_annual: 'P&L annuel',
    balance_sheet: 'Bilan',
    tax_report: 'Rapport fiscal',
    cim: 'CIM',
    evaluation: 'Évaluation',
    contract: 'Contrat',
    license: 'Licence',
    other: 'Autre',
  },
};

// Category labels
export const CATEGORY_LABELS: Record<string, Record<DocumentCategory, string>> = {
  en: {
    financials: 'Financials',
    legal: 'Legal',
    cim: 'CIM',
    licenses: 'Licenses',
    evaluations: 'Evaluations',
    misc: 'Miscellaneous',
  },
  fr: {
    financials: 'Financiers',
    legal: 'Légaux',
    cim: 'CIM',
    licenses: 'Licences',
    evaluations: 'Évaluations',
    misc: 'Divers',
  },
};

// Role labels
export const ROLE_LABELS: Record<string, Record<VisibilityRole, string>> = {
  en: {
    seller: 'Seller',
    buyer: 'Buyer',
    broker: 'Broker',
    lender: 'Lender',
    consultant: 'Consultant',
    partner: 'Partner',
    staff: 'Staff',
    admin: 'Admin',
  },
  fr: {
    seller: 'Vendeur',
    buyer: 'Acheteur',
    broker: 'Courtier',
    lender: 'Prêteur',
    consultant: 'Consultant',
    partner: 'Partenaire',
    staff: 'Staff',
    admin: 'Admin',
  },
};

// Permission rules by role
export const PERMISSION_RULES: PermissionRule[] = [
  {
    role: 'admin',
    canViewNda: true,
    canViewCim: true,
    canViewFinancials: true,
    canUpload: true,
    canShare: true,
  },
  {
    role: 'staff',
    canViewNda: true,
    canViewCim: true,
    canViewFinancials: true,
    canUpload: true,
    canShare: true,
  },
  {
    role: 'seller',
    canViewNda: true,
    canViewCim: true,
    canViewFinancials: true,
    canUpload: true,
    canShare: true,
  },
  {
    role: 'broker',
    canViewNda: true,
    canViewCim: true,
    canViewFinancials: true,
    canUpload: true,
    canShare: true,
  },
  {
    role: 'buyer',
    canViewNda: true,
    canViewCim: true,
    canViewFinancials: true,
    canUpload: true,
    canShare: false, // Basic buyers cannot share
    requiresSubscription: ['essential', 'essential_plus'],
  },
  {
    role: 'lender',
    canViewNda: true,
    canViewCim: true,
    canViewFinancials: true,
    canUpload: false,
    canShare: true,
    requiresNdaSigned: true,
  },
  {
    role: 'consultant',
    canViewNda: false,
    canViewCim: false,
    canViewFinancials: false,
    canUpload: true,
    canShare: true,
    // Visibility depends on mandate
  },
  {
    role: 'partner',
    canViewNda: false,
    canViewCim: false,
    canViewFinancials: false,
    canUpload: true,
    canShare: true,
    // Visibility depends on mandate
  },
];

// Accepted file types
export const ACCEPTED_FILE_TYPES = {
  documents: '.pdf,.doc,.docx',
  spreadsheets: '.xls,.xlsx,.csv',
  images: '.jpg,.jpeg,.png,.gif',
  archives: '.zip,.rar',
};

export const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB

// Translations
export const DEAL_BOX_TRANSLATIONS = {
  en: {
    title: 'Secure Deal Box',
    subtitle: 'Manage and share deal documents securely',
    uploadFile: 'Upload File',
    uploadNew: 'Upload New Document',
    replaceFile: 'Replace File',
    shareFile: 'Share File',
    downloadFile: 'Download',
    deleteFile: 'Delete',
    viewHistory: 'View History',
    filterByType: 'Filter by Type',
    filterByCategory: 'Filter by Category',
    filterByUploader: 'Filter by Uploader',
    filterByDate: 'Filter by Date',
    searchPlaceholder: 'Search documents...',
    noDocuments: 'No documents found',
    version: 'Version',
    uploadedBy: 'Uploaded by',
    uploadedAt: 'Uploaded at',
    fileSize: 'File size',
    visibility: 'Visibility',
    sharedWith: 'Shared with',
    selectDocumentType: 'Select document type',
    selectCategory: 'Select category',
    enterTitle: 'Enter document title',
    enterDescription: 'Enter description (optional)',
    selectVisibility: 'Select who can view this document',
    uploading: 'Uploading...',
    uploadSuccess: 'Document uploaded successfully',
    uploadError: 'Failed to upload document',
    shareSuccess: 'Document shared successfully',
    shareError: 'Failed to share document',
    deleteConfirm: 'Are you sure you want to delete this document?',
    deleteSuccess: 'Document deleted successfully',
    deleteError: 'Failed to delete document',
    permissionDenied: 'You do not have permission to view this document',
    ndaRequired: 'NDA signature required to view this document',
    allTypes: 'All Types',
    allCategories: 'All Categories',
    allUploaders: 'All Uploaders',
    recentFirst: 'Recent First',
    oldestFirst: 'Oldest First',
    clearFilters: 'Clear Filters',
  },
  fr: {
    title: 'Boîte de Dépôt Sécurisée',
    subtitle: 'Gérez et partagez les documents du deal en toute sécurité',
    uploadFile: 'Téléverser un fichier',
    uploadNew: 'Ajouter un document',
    replaceFile: 'Remplacer le fichier',
    shareFile: 'Partager',
    downloadFile: 'Télécharger',
    deleteFile: 'Supprimer',
    viewHistory: 'Voir l\'historique',
    filterByType: 'Filtrer par type',
    filterByCategory: 'Filtrer par catégorie',
    filterByUploader: 'Filtrer par auteur',
    filterByDate: 'Filtrer par date',
    searchPlaceholder: 'Rechercher des documents...',
    noDocuments: 'Aucun document trouvé',
    version: 'Version',
    uploadedBy: 'Téléversé par',
    uploadedAt: 'Téléversé le',
    fileSize: 'Taille',
    visibility: 'Visibilité',
    sharedWith: 'Partagé avec',
    selectDocumentType: 'Sélectionner le type de document',
    selectCategory: 'Sélectionner la catégorie',
    enterTitle: 'Entrer le titre du document',
    enterDescription: 'Entrer une description (optionnel)',
    selectVisibility: 'Sélectionner qui peut voir ce document',
    uploading: 'Téléversement...',
    uploadSuccess: 'Document téléversé avec succès',
    uploadError: 'Échec du téléversement',
    shareSuccess: 'Document partagé avec succès',
    shareError: 'Échec du partage',
    deleteConfirm: 'Êtes-vous sûr de vouloir supprimer ce document ?',
    deleteSuccess: 'Document supprimé avec succès',
    deleteError: 'Échec de la suppression',
    permissionDenied: 'Vous n\'avez pas la permission de voir ce document',
    ndaRequired: 'Signature du NDA requise pour voir ce document',
    allTypes: 'Tous les types',
    allCategories: 'Toutes les catégories',
    allUploaders: 'Tous les auteurs',
    recentFirst: 'Plus récent',
    oldestFirst: 'Plus ancien',
    clearFilters: 'Effacer les filtres',
  },
};
